'use client'

import { FormInput as AriaKitFormInput, useFormContext, useStoreState } from '@ariakit/react'
import { ComponentProps, useId } from 'react'

import { Input } from '@syconium/little-miss-figgy/dist/components/Input/Input'

export type FormInputProps = {
  name: string
} & Omit<ComponentProps<typeof Input>, 'name'>

/**
 * FormInput component that integrates Input from little-miss-figgy with Ariakit form state.
 * Automatically handles error display based on form validation.
 *
 * @example
 * const form = useFormStore({ defaultValues: { email: '' } })
 * <Form store={form}>
 *   <FormInput name="email" type="email" placeholder="Enter email" />
 * </Form>
 */
export const FormInput = ({ name, type, kind, ...rest }: FormInputProps) => {
  const form = useFormContext()
  const inputId = useId()

  // Get field error and touched state from form store
  const error = useStoreState(form, () => form?.getError(name))
  const touched = useStoreState(form, () => form?.getFieldTouched(name))

  const hasError = error !== undefined && error !== null && error.length > 0
  const displayError = touched && hasError

  return (
    <AriaKitFormInput
      name={name}
      render={htmlProps => (
        <Input
          {...rest}
          {...htmlProps}
          type={type}
          kind={kind}
          id={inputId}
          isInvalid={displayError}
          messageWhenInvalid={displayError && error ? error : undefined}
        />
      )}
    />
  )
}
